import {verifiedFetch} from '@github-ui/verified-fetch'
import {AlertIcon, LinkExternalIcon, PlusIcon, ToolsIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Dialog, TextInput} from '@primer/react'
import type React from 'react'
import {type RefObject, useCallback, useRef, useState} from 'react'

import styles from './ConfigureMcpServersMenu.module.css'

export interface ConfigureMcpServersMenuProps {
  anchorRef: RefObject<HTMLButtonElement>
  panel: 'add-mcp' | null
  onPanelChange: (panel: 'add-mcp' | null) => void
}

export const ConfigureMcpServersMenu: React.FC<ConfigureMcpServersMenuProps> = ({panel, onPanelChange}) => {
  const [showDialog, setShowDialog] = useState(false)
  const open = panel === 'add-mcp' || showDialog
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)

  // When dialog closes, just hide dialog, keep overlay open
  const onDialogClose = useCallback(() => {
    setShowDialog(false)
    setName('')
    setUrl('')
    setUrlError(null)
    onPanelChange(null)
  }, [onPanelChange])

  // When overlay closes, also hide dialog and reset form
  const handleOverlayClose = useCallback(() => {
    if (!showDialog) {
      onPanelChange(null)
      setShowDialog(false)
      setName('')
      setUrl('')
      setUrlError(null)
    }
  }, [onPanelChange, showDialog])

  function isValidHttpsUrl(inputUrl: string): boolean {
    try {
      const parsedUrl = new URL(inputUrl.trim(), window.location.origin)
      return (
        parsedUrl.protocol === 'https:' &&
        !!parsedUrl.hostname &&
        /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(parsedUrl.hostname)
      )
    } catch {
      return false
    }
  }

  return (
    <>
      <AnchoredOverlay
        open={open}
        side="outside-top"
        align="start"
        onOpen={() => onPanelChange('add-mcp')}
        onClose={handleOverlayClose}
        renderAnchor={props => (
          <Button {...props} leadingVisual={ToolsIcon} variant="invisible" aria-label="MCPs">
            MCPs
          </Button>
        )}
      >
        <div className={styles.overlayContent}>
          {panel === 'add-mcp' && !showDialog && (
            <Button
              variant="invisible"
              leadingVisual={PlusIcon}
              ref={buttonRef}
              onClick={e => {
                e.stopPropagation()
                setShowDialog(true)
              }}
              className={styles.addMcpButton}
            >
              Add MCP
            </Button>
          )}
        </div>
      </AnchoredOverlay>
      {showDialog && (
        <Dialog
          title="Add remote MCP"
          onClose={onDialogClose}
          footerButtons={[
            {buttonType: 'default', content: 'Cancel', onClick: onDialogClose},
            {
              buttonType: 'primary',
              content: (
                <>
                  Connect to MCP
                  <span style={{marginLeft: 8}}>
                    <LinkExternalIcon size={16} />
                  </span>
                </>
              ),
              onClick: () => {
                void (async () => {
                  try {
                    setUrlError(null)
                    // Enhanced URL validation using helper
                    if (!isValidHttpsUrl(url)) {
                      setUrlError('Invalid OAuth URL')
                      return
                    }
                    const formData = new FormData()
                    formData.append('mcp_server_url', url.trim())
                    formData.append('name', name.trim())
                    const response = await verifiedFetch('/copilot/mcp/servers', {
                      method: 'POST',
                      body: formData,
                    })

                    if (!response.ok) {
                      const errorText = await response.text()
                      throw new Error(errorText || 'Failed to add MCP server')
                    }

                    const data = await response.json()
                    if (response.redirected) {
                      window.location.href = data.redirect_url
                      onDialogClose()
                    }
                  } catch (error: unknown) {
                    if (error instanceof Error) {
                      alert(error.message)
                    } else {
                      alert('An unknown error occurred')
                    }
                  }
                })()
              },
            },
          ]}
          returnFocusRef={buttonRef}
        >
          <form className={styles.dialogForm} onSubmit={e => e.preventDefault()}>
            <div className={styles.formField}>
              <label htmlFor="mcp-name">Name</label>
              <TextInput
                id="mcp-name"
                name="name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter a name for this MCP"
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="mcp-url">MCP server URL</label>
              <TextInput
                id="mcp-url"
                name="url"
                type="url"
                value={url}
                onChange={e => {
                  setUrl(e.target.value)
                  setUrlError(null)
                }}
                placeholder="https://mcp.example.com/sse"
                className={urlError ? styles.inputError : undefined}
              />
              {urlError && (
                <div style={{color: 'red', marginTop: 4, display: 'flex', alignItems: 'center'}}>
                  <span style={{marginRight: 4}}>
                    <AlertIcon size={16} />
                  </span>
                  {urlError}
                </div>
              )}
            </div>
            <p>
              MCP data will be processed by models coming from third-party providers. Always review the permissions
              required by the MCP and only connect to MCPs you know and trust. You can revoke access for the MCP at any
              time in{' '}
              <a href="/settings" className={styles.underline}>
                your settings
              </a>
              .
            </p>
          </form>
        </Dialog>
      )}
    </>
  )
}

try{ ConfigureMcpServersMenu.displayName ||= 'ConfigureMcpServersMenu' } catch {}